import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Upload, FileCheck2, X, Phone, User as UserIcon, ShieldCheck, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function QuickPrescriptionCard() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File must be under 8 MB");
      return;
    }
    setFile(f);
  };
  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\d{10}$/.test(phone)) return toast.error("Phone must be 10 digits");
    if (!file) return toast.error("Please attach your prescription");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("phone", phone);
      fd.append("note", note.trim());
      fd.append("file", file);
      await axios.post(`${BACKEND}/api/prescription-requests`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Prescription received", {
        description: "Our pharmacist will call you back within 30 minutes.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setName(""); setPhone(""); setNote(""); setFile(null); setSubmitted(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-24" data-testid="rx-request-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative rounded-lg overflow-hidden border border-[#1A362B]/15 bg-gradient-to-br from-[#1A362B] via-[#204236] to-[#1A362B] text-[#EFE7D6]"
      >
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #E8C170 0%, transparent 40%), radial-gradient(circle at 85% 70%, #D96C4A 0%, transparent 45%)" }} />
        <div className="relative grid lg:grid-cols-5 gap-0">
          <div className="lg:col-span-2 p-8 lg:p-12 flex flex-col justify-center">
            <div className="label-caps text-[#E8C170] mb-4" data-testid="rx-request-eyebrow">
              ★ Skip the line
            </div>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight tracking-tight">
              Send us your <em className="italic text-[#E8C170]">prescription</em>.
            </h2>
            <p className="mt-5 text-stone-300 text-sm leading-relaxed max-w-md">
              Snap a photo of your doctor&apos;s prescription. Our pharmacist reads it, quotes the price on WhatsApp, and dispatches the same day — no account needed to start.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-stone-300">
              <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-[#E8C170]"/> Pharmacist-verified · privacy first</li>
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#E8C170]"/> Call back within 30 minutes on WhatsApp</li>
              <li className="flex items-center gap-3"><FileCheck2 className="w-4 h-4 text-[#E8C170]"/> Pay Cash on Delivery or online, your choice</li>
            </ul>
          </div>

          <div className="lg:col-span-3 p-8 lg:p-12 bg-[#EFE7D6]/[0.04] backdrop-blur-sm border-l border-[#EFE7D6]/10">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-8" data-testid="rx-request-success">
                <div className="w-14 h-14 rounded-full bg-[#E8C170]/20 border border-[#E8C170] flex items-center justify-center">
                  <FileCheck2 className="w-6 h-6 text-[#E8C170]"/>
                </div>
                <h3 className="mt-6 font-serif-display text-2xl">Prescription received</h3>
                <p className="mt-3 text-sm text-stone-300 max-w-sm">
                  Our pharmacist will call you back on <span className="text-[#E8C170] font-mono-num">+91 {phone}</span> within 30 minutes with a quote.
                </p>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="mt-8 rounded-full border-[#E8C170]/50 text-[#E8C170] hover:bg-[#E8C170] hover:text-[#1A362B]"
                  data-testid="rx-request-send-another"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] label-caps text-[#E8C170]/80">Your name</Label>
                    <div className="relative mt-1">
                      <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Ramesh Sharma"
                        className="pl-9 bg-[#EFE7D6]/5 border-[#EFE7D6]/20 text-[#EFE7D6] placeholder:text-stone-500 focus-visible:ring-[#E8C170]"
                        data-testid="rx-request-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] label-caps text-[#E8C170]/80">WhatsApp number</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-mono-num">+91</span>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        placeholder="10-digit number"
                        maxLength={10}
                        inputMode="numeric"
                        className="pl-11 bg-[#EFE7D6]/5 border-[#EFE7D6]/20 text-[#EFE7D6] placeholder:text-stone-500 focus-visible:ring-[#E8C170] font-mono-num"
                        data-testid="rx-request-phone"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] label-caps text-[#E8C170]/80">Attach prescription</Label>
                  {file ? (
                    <div className="mt-1 flex items-center gap-3 p-3 rounded-md bg-[#E8C170]/10 border border-[#E8C170]/40" data-testid="rx-request-file">
                      <FileCheck2 className="w-5 h-5 text-[#E8C170]"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{file.name}</div>
                        <div className="text-[10px] text-stone-400">{(file.size / 1024).toFixed(0)} KB · {file.type}</div>
                      </div>
                      <button onClick={clearFile} className="text-stone-400 hover:text-[#D96C4A]" data-testid="rx-request-file-remove"><X className="w-4 h-4"/></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-[#EFE7D6]/25 hover:border-[#E8C170] rounded-md p-6 cursor-pointer transition-colors text-center bg-[#EFE7D6]/[0.02]" data-testid="rx-request-upload-box">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                        onChange={onFile}
                        className="hidden"
                        data-testid="rx-request-file-input"
                      />
                      <Upload className="w-5 h-5 text-[#E8C170]"/>
                      <div className="mt-2 text-sm">Tap to upload photo or PDF</div>
                      <div className="mt-1 text-[10px] text-stone-400">JPG · PNG · PDF · up to 8 MB</div>
                    </label>
                  )}
                </div>

                <div>
                  <Label className="text-[10px] label-caps text-[#E8C170]/80">Notes (optional)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    placeholder="e.g. Please deliver by evening, doorbell not working"
                    rows={2}
                    className="mt-1 bg-[#EFE7D6]/5 border-[#EFE7D6]/20 text-[#EFE7D6] placeholder:text-stone-500 focus-visible:ring-[#E8C170] resize-none"
                    data-testid="rx-request-note"
                  />
                </div>

                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white h-12 shine active:scale-95 transition-transform"
                  data-testid="rx-request-submit"
                >
                  {submitting ? "Sending…" : <>Send prescription <Send className="w-4 h-4 ml-2"/></>}
                </Button>
                <p className="text-[10px] text-stone-400 text-center label-caps">
                  Your prescription stays private · shared only with our pharmacist
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
